/**
 * SignFilePage — Wizard 1.
 *
 * Step 1: drop zone (drag-and-drop or click-to-browse).
 * Step 2: GiOrbit spinner while we hash the file with Web Crypto SHA-256.
 *         The file never leaves the browser.
 * Step 3: "Done!" — show filename, short hash, timestamp, download proof,
 *         offer "Sign Another".
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Check, Download } from 'lucide-react';

import { Button } from '@/components/Button';
import { GiOrbitLogo } from '@/components/GiOrbitLogo';
import { WizardShell } from '@/layouts/WizardShell';
import { useAuthStore } from '@/stores/authStore';
import { useProofsStore, buildProofCertificate, type Proof } from '@/stores/proofsStore';
import { useCreateProof } from '@/hooks/useProofs';
import { sha256OfFile, downloadJson, shortHash } from '@/utils/hashing';
import { cn } from '@/utils/cn';

type Step = 'drop' | 'signing' | 'done';

export function SignFilePage() {
  const { t } = useTranslation('wizards');
  const user = useAuthStore((s) => s.user);
  const addProof = useProofsStore((s) => s.add);
  const createProof = useCreateProof();

  const [step, setStep] = useState<Step>('drop');
  const [proof, setProof] = useState<Proof | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const stepNum = step === 'drop' ? 1 : step === 'signing' ? 2 : 3;

  const handleFile = async (file: File) => {
    setError(null);
    setStep('signing');
    try {
      const hash = await sha256OfFile(file);
      const created = addProof({
        kind: 'file',
        name: file.name,
        hash,
        signer: user?.email ?? 'anonymous',
        bytes: file.size,
      });
      // Await the server POST. A failed POST throws into the catch
      // below, returns the wizard to 'drop' with the error visible —
      // no more silent "Done!" for proofs that were never persisted.
      // The offline cache already holds `created` so a future mount's
      // resync hook will re-attempt the POST regardless.
      await createProof.mutateAsync(created);
      setProof(created);
      setStep('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown_error');
      setStep('drop');
    }
  };

  return (
    <WizardShell title={t('sign_file.page_title')} totalSteps={3} currentStep={stepNum}>
      {step === 'drop' ? (
        <DropStep onFile={handleFile} inputRef={inputRef} error={error} />
      ) : step === 'signing' ? (
        <SigningStep />
      ) : (
        <DoneStep proof={proof} onAnother={() => { setProof(null); setStep('drop'); }} />
      )}
    </WizardShell>
  );
}

function DropStep({
  onFile,
  inputRef,
  error,
}: {
  onFile: (file: File) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  error: string | null;
}) {
  const { t } = useTranslation('wizards');
  const [hover, setHover] = useState(false);

  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setHover(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFile(file);
  };

  return (
    <div className="space-y-4">
      <label
        htmlFor="sign-file-input"
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={onDrop}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 text-center transition-colors',
          hover ? 'border-spark bg-spark-soft' : 'border-border bg-canvas hover:bg-surface-raised',
        )}
      >
        <Upload className="mb-3 h-8 w-8 text-ink-muted" aria-hidden />
        <p className="text-base font-medium text-ink">{t('sign_file.drop_title')}</p>
        <p className="mt-1 text-sm text-ink-muted">{t('sign_file.drop_subtitle')}</p>
        <input
          ref={inputRef}
          id="sign-file-input"
          type="file"
          className="sr-only"
          onChange={onChange}
        />
      </label>
      <p className="text-center text-xs text-ink-subtle">{t('sign_file.drop_hint')}</p>
      {error ? (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function SigningStep() {
  const { t } = useTranslation('wizards');
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-12">
      <GiOrbitLogo size="lg" />
      <p className="text-base font-medium">{t('sign_file.spinning')}</p>
      <p className="text-sm text-ink-muted">{t('sign_file.spinning_hint')}</p>
    </div>
  );
}

function DoneStep({ proof, onAnother }: { proof: Proof | null; onAnother: () => void }) {
  const { t, i18n } = useTranslation('wizards');
  // Keep last successful proof visible while the user clicks Download.
  const [last, setLast] = useState(proof);
  useEffect(() => { if (proof) setLast(proof); }, [proof]);
  if (!last) return null;

  const handleDownload = () => {
    const cert = buildProofCertificate(last);
    downloadJson(`${slug(last.name)}.proof.json`, cert);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success">
          <Check className="h-7 w-7" aria-hidden />
        </span>
        <h2 className="text-xl font-semibold">{t('common.done_title')}</h2>
        <p className="text-sm text-ink-muted">{t('common.done_file')}</p>
      </div>

      <dl className="space-y-3 rounded-lg border border-border bg-canvas p-4 text-sm">
        <Row label={t('common.filename')} value={last.name} />
        <Row label={t('common.hash_short')} value={shortHash(last.hash, 12, 12)} mono />
        <Row label={t('common.timestamp')} value={new Date(last.timestamp).toLocaleString(i18n.language)} />
        <Row label={t('common.signer')} value={last.signer} />
      </dl>

      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onAnother}>
          {t('common.another')}
        </Button>
        <Button onClick={handleDownload} leading={<Download className="h-4 w-4" aria-hidden />}>
          {t('common.download_proof')}
        </Button>
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
      <dt className="text-xs uppercase tracking-wider text-ink-subtle">{label}</dt>
      <dd className={cn('break-all text-sm text-ink', mono && 'font-mono')}>{value}</dd>
    </div>
  );
}

function slug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9-_]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64) || 'proof';
}
